import { motion, AnimatePresence } from 'framer-motion'
import { X, Gift, Zap } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import { EmailForm } from './EmailForm'

interface ExitIntentPopupProps {
  isOpen: boolean
  onClose: () => void
}

export function ExitIntentPopup({ isOpen, onClose }: ExitIntentPopupProps) {
  const { t } = useLanguage()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-midnight-950/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="relative w-full max-w-lg">
              {/* Glow Effect */}
              <div
                className="absolute inset-0 -z-10 rounded-3xl opacity-50"
                style={{
                  background: 'radial-gradient(circle at center, rgba(247, 208, 70, 0.3) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                }}
              />

              {/* Card */}
              <div className="glass-card p-8 relative overflow-hidden">
                {/* Animated Border */}
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background: 'linear-gradient(90deg, #f7d046, #e8b923, #c99a13, #e8b923, #f7d046)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s linear infinite',
                    padding: '2px',
                    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    maskComposite: 'xor',
                    WebkitMaskComposite: 'xor',
                  }}
                />

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-200 transition-colors z-10"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="relative z-10 text-center">
                  {/* Icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30, delay: 0.2 }}
                    className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-500/20 to-gold-600/20 mb-6"
                  >
                    <Gift className="w-8 h-8 text-gold-400" />
                  </motion.div>

                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-500/10 border border-gold-500/30 rounded-full mb-4">
                    <Zap className="w-3 h-3 text-gold-400" />
                    <span className="text-gold-400 text-xs font-medium">Founding Member</span>
                  </div>

                  {/* Headline */}
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-3">
                    {t.exitPopup.headline}
                  </h3>
                  <p className="text-slate-400 mb-8">
                    {t.exitPopup.subheadline}
                  </p>

                  {/* Email Form */}
                  <div className="mb-6">
                    <EmailForm variant="compact" onSuccess={onClose} />
                  </div>

                  {/* Dismiss Link */}
                  <button
                    onClick={onClose}
                    className="text-sm text-slate-500 hover:text-slate-400 transition-colors"
                  >
                    {t.exitPopup.dismiss}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
